import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { LoadingList } from "@/components/common/LoadingList";
import { api } from "@/lib/api";
import { fmtDateTime, fmtHours, fmtMoney, fmtNumber } from "@/lib/format";
import { AUDIT_LABELS, STATUS_LABELS, unitShort } from "@/lib/i18n";

const detailText = (event, isAdmin) => {
  const d = event.details || {};
  switch (event.action) {
    case "ordre_tildelt":
      return d.assigned?.length ? `Tildelt: ${d.assigned.join(", ")}` : "Ingen tildelte";
    case "ordre_endra":
      return d.fields?.length ? `Felt: ${d.fields.join(", ")}` : null;
    case "registrering_oppretta":
    case "registrering_sletta": {
      const parts = [];
      if (d.item) parts.push(d.item);
      if (d.hours != null) parts.push(fmtHours(d.hours));
      if (d.quantity != null) parts.push(`${fmtNumber(d.quantity, 3)} ${unitShort(d.unit)}`);
      if (d.for) parts.push(`for ${d.for}`);
      return parts.join(" · ") || null;
    }
    case "status_i_arbeid":
    case "status_utfort":
    case "status_avlyst":
    case "status_gjenopna":
      return [d.from && d.to ? `${STATUS_LABELS[d.from] || d.from} → ${STATUS_LABELS[d.to] || d.to}` : null, d.auto ? "(automatisk ved første registrering)" : null, d.note ? `«${d.note}»` : null].filter(Boolean).join(" ");
    case "vedlegg_lasta_opp":
    case "vedlegg_sletta":
      return d.filename || null;
    case "fakturagrunnlag_godkjent":
      return isAdmin ? `Revisjon ${d.revision} · ${fmtMoney(d.total_excl_vat)} eks. mva` : null;
    case "fakturagrunnlag_oppretta":
    case "fakturagrunnlag_endra":
    case "fakturagrunnlag_regenerert":
    case "fakturagrunnlag_forkasta":
      return d.revision ? `Revisjon ${d.revision}` : null;
    case "eksport_oppretta":
      return `${(d.format || "").toUpperCase()} · revisjon ${d.revision}`;
    case "eksport_handtert":
      return `Revisjon ${d.revision} · ${d.note || ""}`;
    case "fakturert_manuelt":
      return `Revisjon ${d.revision} · faktura ${d.invoice_reference} (${d.invoice_date})`;
    default:
      return null;
  }
};

export const HistorikkPanel = ({ order, isAdmin }) => {
  const query = useQuery({ queryKey: ["history", order.id], queryFn: async () => (await api.get(`/orders/${order.id}/history`)).data });
  if (query.isLoading) return <LoadingList rows={3} />;
  if (!query.data?.length) return <EmptyState icon={History} title="Ingen hendingar" />;
  return (
    <ol className="relative space-y-0 border-l border-border pl-5" data-testid="historikk-list">
      {query.data.map((ev) => (
        <li key={ev.id} className="relative pb-5" data-testid="historikk-item">
          <span className="absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-primary" aria-hidden="true" />
          <p className="text-sm font-medium">{AUDIT_LABELS[ev.action] || ev.action}</p>
          {detailText(ev, isAdmin) ? <p className="text-sm text-muted-foreground">{detailText(ev, isAdmin)}</p> : null}
          <p className="text-xs text-muted-foreground"><span className="num">{fmtDateTime(ev.at)}</span> · {ev.actor_name}</p>
        </li>
      ))}
    </ol>
  );
};
